// Shared client-side field-validation helpers (spec: field-validation-hardening, AC8/AC9).
// Mirrors services/backend/validation.py — keep the two in sync so a value the form
// accepts is never rejected by the API, and vice versa.
import type { KeyboardEvent } from "react";

/** Required text: whitespace-only counts as empty (AC14). Returns an error message or null. */
export function requiredTrimmed(value: string | null | undefined, label: string): string | null {
  if (!value || !value.trim()) return `${label} is required`;
  return null;
}

/** Required amount that must be strictly greater than zero. Returns an error message or null. */
export function positiveAmount(value: string | number | null | undefined, label: string): string | null {
  const n = typeof value === "number" ? value : parseFloat(value ?? "");
  if (value === "" || value == null || !isFinite(n)) return `${label} is required`;
  if (n <= 0) return `${label} must be greater than zero`;
  return null;
}

/** Required value of any kind (dates, selects). Returns an error message or null. */
export function requiredValue(value: string | null | undefined, label: string): string | null {
  if (!value) return `${label} is required`;
  return null;
}

/**
 * Keydown handler for <input type="number"> fields (C4-4): browsers let users type
 * "e"/"E" (scientific notation) and "+" into number inputs with no validation error,
 * silently accepting non-numeric-looking entries. Blocks those keys; "-" is allowed
 * only when the field permits negative values.
 */
export function blockInvalidNumberKey(e: KeyboardEvent<HTMLInputElement>, allowNegative = false): void {
  const blocked = allowNegative ? ["e", "E", "+"] : ["e", "E", "+", "-"];
  if (blocked.includes(e.key)) e.preventDefault();
}

/**
 * Reject a date beyond today. Mirrors validation.py's `not_future_date`.
 *
 * No grace window here, deliberately: this runs in the browser, so `new Date()` is
 * already the user's own local today — the thing the backend's one-day grace exists to
 * approximate from UTC. Adding a grace on both sides would stack them and let a real
 * future date through.
 *
 * That makes this side equal-or-stricter than the API, never looser, which is the safe
 * direction for the pair: the form can't accept something the API would then reject.
 */
export function notFutureDate(value: string | null | undefined, label: string): string | null {
  if (!value) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Parse as local midnight, not UTC — `new Date("2026-07-31")` is parsed as UTC by
  // spec and would land on the previous day for anyone west of Greenwich.
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;   // not a date this helper understands; leave it alone
  const parsed = new Date(y, m - 1, d);
  if (parsed > today) return `${label} cannot be in the future`;
  return null;
}

/** Today as YYYY-MM-DD in the user's own timezone — for a date input's `max`. */
export function todayLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Collect field errors: pass {field: errorOrNull}; returns only the real errors. */
export function collectErrors(checks: Record<string, string | null>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [field, err] of Object.entries(checks)) {
    if (err) errors[field] = err;
  }
  return errors;
}
